/**
 * i18n. Three languages, all three filled in.
 *
 * The rule is the admin's rule: no user-facing string is written inside a component.
 * A hardcoded Russian label is a bug — the moment a translator opens `uz.json` that
 * string is invisible to them, and it stays Russian on a device set to Uzbek forever
 * after, with nothing anywhere reporting it.
 *
 * ## Which language a device gets
 *
 * An explicit choice, if the user made one (persisted; see `applyStoredLang`), else
 * the system language, else Russian. The stored choice is read ASYNCHRONOUSLY on
 * start-up, so i18n initialises on the device language first and switches a tick
 * later — that is deliberate: blocking the first render on a disk read to avoid one
 * frame in the wrong language is the wrong trade on a cold start.
 *
 * ## Plurals
 *
 * Russian has four plural categories (`_one/_few/_many/_other`), Uzbek and English
 * two (`_one/_other`). i18next picks the categories per language, so the files
 * legitimately differ in key COUNT while covering the same keys — which is why the
 * completeness check compares plural keys by their base name, not literally.
 */
import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';
import {NativeModules, Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import en from './en.json';
import ru from './ru.json';
import uz from './uz.json';

export const SUPPORTED_LANGS = ['ru', 'uz', 'en'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];
export const DEFAULT_LANG: Lang = 'ru';

const STORAGE_KEY = 'samvision.lang';

/** Human name of each language, in that language — never translated. */
export const LANG_LABELS: Record<Lang, string> = {
  ru: 'Русский',
  uz: 'O‘zbekcha',
  en: 'English',
};

function isLang(value: string | undefined | null): value is Lang {
  return (SUPPORTED_LANGS as readonly string[]).includes(value ?? '');
}

/**
 * The device's language, as a tag we support. React Native exposes no locale API in
 * JS, so this reads the Android system locale off the native settings bridge and
 * treats anything unexpected — including a bridge that is not there under Jest — as
 * the default.
 *
 * On web the browser answers directly, which also covers a desktop tester who has
 * never touched an Android setting.
 */
export function deviceLang(): Lang {
  const tag =
    Platform.OS === 'web'
      ? (globalThis as {navigator?: {language?: string}}).navigator?.language
      : (NativeModules.I18nManager?.localeIdentifier as string | undefined);
  const primary = tag?.split(/[-_]/)[0]?.toLowerCase();
  return isLang(primary) ? primary : DEFAULT_LANG;
}

void i18n.use(initReactI18next).init({
  resources: {
    ru: {common: ru},
    uz: {common: uz},
    en: {common: en},
  },
  lng: deviceLang(),
  fallbackLng: DEFAULT_LANG,
  supportedLngs: SUPPORTED_LANGS as unknown as string[],
  defaultNS: 'common',
  ns: ['common'],
  // React already escapes what it renders; escaping again would print &#39; to users.
  interpolation: {escapeValue: false},
  returnNull: false,
});

/** The language in force right now, narrowed to one we ship. */
export function currentLang(): Lang {
  return isLang(i18n.resolvedLanguage) ? i18n.resolvedLanguage : DEFAULT_LANG;
}

/** Switch language and remember the choice across launches. */
export async function setLang(lang: Lang): Promise<void> {
  await i18n.changeLanguage(lang);
  try {
    await AsyncStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // A failed write costs the user their choice on the NEXT launch, not this one.
    // Refusing to switch because storage is full would cost it immediately.
  }
}

/**
 * Restore a previously chosen language. Called once at start-up; does nothing when
 * the user has never chosen, which leaves the device language in force.
 */
export async function applyStoredLang(): Promise<void> {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    if (isLang(stored) && stored !== i18n.resolvedLanguage) {
      await i18n.changeLanguage(stored);
    }
  } catch {
    // Unreadable storage means the device language stands. Nothing to report.
  }
}

export default i18n;
